/**
 * To Display all the brands sorted by alphabets
 *
 * @module views/components/ShopAllBrands
 * @memberof -Common
 */
import './ShopAllBrands.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';

import InputFieldUnderline from '@ulta/core/components/InputFieldUnderline/InputFieldUnderline';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import * as datacapture from '@ulta/core/utils/datacapture/datacapture';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import AnchorList from '@ulta/components/AnchorList/AnchorList';
import BrandListSection from '@ulta/components/BrandListSection/BrandListSection';

import * as utils from '@ulta/hooks/useAnchorList/useAnchorList';

import * as util from './ShopAllBrands';
/**
 * Represents a ShopAllBrands component
 *
 * @method
 * @param {ShopAllBrandsProps} props - React properties passed from composition
 * @returns ShopAllBrands
 */
export const ShopAllBrands = function( props ){
  const { allBrandsTitle, navItems, resultsLabel, brandSearchBar, brandAccessibilityLabel, skipAllLinksLabel } = props;
  const searchAction = brandSearchBar?.searchAction;
  const searchEvent = brandSearchBar?.searchAction?.dataCaptureData;
  const [filteredItem, setFilteredItem] = useState( navItems );
  const [searchInput, setSearchInput] = useState( '' );
  const ref = useRef();
  const debounceDcEventRef = useRef( null );
  const [searchCount, setSearchCount] = useState( 0 );
  const previousInput = useRef( '' );
  const { init,
    reportItemScrolling,
    shouldOffsetElement,
    headerOffset,
    onItemScrolled,
    positionElementRef,
    offsetElementRef,
    setScrollTarget,
    selectedLetter,
    setSelectedLetter,
    setReportItemScrolling } = utils.useAnchorList();



  const onAnchorLetterClick = useCallback(
    utils.getAnchorLetterClick(
      { navItems },
      { setSearchCount, setFilteredItem, setSearchInput, setScrollTarget, setSelectedLetter, setReportItemScrolling }
    ),
    []
  );
  const dataCaptureHandler = useCallback( util.composeDataCaptureHandler( { searchEvent } ), [] );
  const searchChangeHandler = useCallback( ( input ) => {
    util.composeSearchChangeHandler(
      { debounceDcEventRef, action: searchAction, previousInput, navItems, filteredItem, letter: 'A', input },
      { dataCaptureHandler, setSearchInput, setSearchCount, setFilteredItem, setScrollTarget, setSelectedLetter }
    );
  }, [] );

  // Watch searchCount to disable/enable brand section scroll reporting
  useEffect( () => {
    utils.handleItemScrollReporting( { init, searchCount }, { setReportItemScrolling } );
  }, [searchCount, init] );


  return (
    <div
      className='ShopAllBrands'
    >
      <div className='ShopAllBrands__content'>
        <Formik
          initialValues={ {
            search: ''
          } }
        >
          { ( { setFieldValue } ) => (
            <>
              <Form className='ShopAllBrands__search'
                role='search'
                aria-label='Brands-Search'
                onSubmit={ e => e.preventDefault() }
              >
                <InputFieldUnderline type='search'
                  name='search'
                  autoComplete='off'
                  leftIcon='Search'
                  placeholder={ brandSearchBar?.placeholderLabel }
                  onChange={ input => searchChangeHandler( input ) }
                  handleResetVal={ () => {
                    utils.resetFilterStateHandler(
                      { letter: 'A', navItems },
                      { setSearchCount, setFilteredItem, setSearchInput, setScrollTarget, setSelectedLetter }
                    );
                  } }
                  ariaDescribedBy='descriptionSearch'
                  clearValueText={ brandSearchBar?.clearAccessibilityLabel }
                  submitText={ brandSearchBar?.searchAction.label }
                />
                { searchCount > 0 && (
                  <div className='ShopAllBrands__skipBrandLink'>
                    <Link_Huge url='#brandresults'
                      id='ShopAllBrands__skipToBrandResults'
                      onClick={ () => utils.setFocusOnElementHandler( { ref, offsetElementRef } ) }
                    >
                      { skipAllLinksLabel }
                    </Link_Huge>
                  </div>
                ) }
                <p className='sr-only'
                  id='descriptionSearch'
                >
                  { props.instructionAccessibilityLabel }
                </p>
              </Form>

              <div className='ShopAllBrands__anchorListPositionEl'
                ref={ positionElementRef }
              />
              <div className='ShopAllBrands__anchorList'
                ref={ offsetElementRef }
                { ...spreadProps( {
                  style: shouldOffsetElement ? {
                    transform: `translateY(${headerOffset}px)`
                  } : undefined
                } ) }
              >
                <AnchorList
                  allBrandsTitle={ allBrandsTitle }
                  navItems={ navItems }
                  selectedLetter={ selectedLetter }
                  brandAccessibilityLabel={ brandAccessibilityLabel }
                  onLetterClick={ ( letter ) => {
                    setFieldValue( 'search', '' );
                    onAnchorLetterClick( letter, 0 );
                  } }
                />
              </div>
            </>
          ) }
        </Formik>
        { searchInput.length > 2 && (
          <div className='ShopAllBrands_searchResults'>
            <div className='ShopAllBrands_searchResultsLabel'
              aria-live='assertive'
              id='brandresults'
              tabIndex='-1'
              ref={ ref }
            >
              <Text textStyle='title-6'
                htmlTag='h3'
              >
                { searchCount } { resultsLabel } "{ searchInput }"
              </Text>
            </div>

          </div>
        ) }
        <div className='ShopAllBrands__brandList'>
          { filteredItem?.length > 0 &&
            filteredItem.map( ( navItem ) => {
              return (
                <BrandListSection
                  key={ `k-${navItem.title}` }
                  brand={ navItem }
                  onScrolled={ reportItemScrolling && onItemScrolled }
                  reportActive={ reportItemScrolling }
                />
              );
            } ) }
        </div>
      </div>
    </div>
  );
};

/**
 * @method composeDataCaptureHandler
 * @summary This method is to capture Datacapture events
 * @param {action} data.searchEvent search action
 * @param {object} eventData datacapture data for click and submit events
 */
export const composeDataCaptureHandler = ( data ) => ( eventData ) => {
  const { searchEvent } = data || {};
  const { searchTerm, searchType } = eventData || {};

  const typeAheadData = { searchTerm, searchType };

  datacapture.emitTypeAheadData( {
    typeAheadData,
    searchEvent: searchEvent
  } );
};


// onChange Handler
/**
 * Handles change user input
 * @method composeSearchChangeHandler
 * @param {object} data - Data arguments
 * @param {object} data.letter - Input letter
 * @param {object} data.previousInput - previous Input
 * @param {array} data.navItems - List of All Brands to display
 * @param {array} data.filteredItem - List of All filtered Brands
 * @param {object} data.input - Input word
 * @param {object} methods - Methods arguments
 * @param {function} methods.setSearchCount - Set search count
 * @param {function} methods.setFilteredItem - set Filtered Brand
 * @param {function} methods.setSearchInput - set Searc hInput
 * @param {function} methods.setScrollTarget - onBlur callback
 * @param {function} methods.setSelectedLetter - set Selected Letter
 */
export const composeSearchChangeHandler = ( data, methods ) => {
  const { debounceDcEventRef = {}, action, previousInput, navItems, filteredItem, letter, input } = data || {};
  const { dataCaptureHandler, setSearchInput, setSelectedLetter, setSearchCount, setFilteredItem, setScrollTarget } = methods || {};

  if(
    !previousInput ||
    !navItems ||
    !filteredItem ||
    !letter ||
    !setSearchInput ||
    !setSelectedLetter ||
    !setSearchCount ||
    !setFilteredItem ||
    !setScrollTarget
  ){
    return;
  }

  const searchTerm = input;
  const searchType = 'all brands';
  clearTimeout( debounceDcEventRef.current );
  debounceDcEventRef.current = setTimeout( () => {
    dataCaptureHandler( { searchTerm, searchType } );
  }, 150 );

  if( previousInput.current?.length > input.length ){
    utils.filterItemsUtilityHandler(
      { brandList: navItems, userInput: input, navItems },
      { setSelectedLetter, setSearchCount, setFilteredItem }
    );
  }
  else {
    utils.filterItemsUtilityHandler(
      { brandList: filteredItem, userInput: input, navItems },
      { setSelectedLetter, setSearchCount, setFilteredItem }
    );
  }

  if( input.length <= 2 ){
    utils.resetFilterStateHandler(
      { letter, navItems },
      { setSearchCount, setFilteredItem, setSearchInput, setScrollTarget, setSelectedLetter }
    );
  }

  previousInput.current = input;
  setSearchInput( input );
};
/**
 * Property type definitions
 * @typedef ShopAllBrandsProps
 * @type {object}
 * @property {string} allBrandsTitle - Sets the title of the brands
 * @property {array} navItems - List of All Brands to display
 * @property {object} brandSearchBar - Object contains brandSearch bar title and action
 * @property {string} resultsLabel - sets the label for the results label
 * @property {string} instructionAccessibilityLabel - Sets instructions for search input accessibility
 * @property {string} brandAccessibilityLabel - Sets the Aria Label of the all Brands Title
 */
export const propTypes = {
  allBrandsTitle: PropTypes.string,
  navItems: PropTypes.arrayOf(
    PropTypes.shape( {
      title: PropTypes.string,
      action: PropTypes.object,
      items: PropTypes.arrayOf(
        PropTypes.shape( {
          action: PropTypes.object,
          badges: PropTypes.array
        } )
      )
    } )
  ),
  brandSearchBar: PropTypes.object,
  resultsLabel: PropTypes.string,
  instructionAccessibilityLabel: PropTypes.string,
  brandAccessibilityLabel: PropTypes.string
};

ShopAllBrands.propTypes = propTypes;

export default ShopAllBrands;